import { useMutation, useQuery } from '@apollo/client';
import { EWorldMapBookingConfigurationFieldType } from '__generated__/globalTypes';
import { getBookingStringTranslation } from 'components/detail/view/DetailBooking/DetailBookingTranslations';
import { IConfirmActionModal } from 'components/site/modals/ModalWrapper/ModalContent/contents/ConfirmActionModal';
import { EModal } from 'components/site/modals/ModalWrapper/ModalContent/useGetModalProperties';
import { ActionButton } from 'elements/minute/sidebar/sidebarComponents/atomic/ActionButton';
import { Message } from 'elements/minute/sidebar/sidebarComponents/atomic/Message';
import { SidebarSectionHeader } from 'elements/minute/sidebar/sidebarSection/SidebarSection/SidebarSectionHeader';
import {
    DeleteBooking,
    DeleteBookingVariables
} from 'graphql/mutation/__generated__/DeleteBooking';
import { DELETE_BOOKING_MUTATION } from 'graphql/mutation/worldMutation';
import {
    BookingQueryEditor,
    BookingQueryEditorVariables
} from 'graphql/query/__generated__/BookingQueryEditor';
import { BOOKING_QUERY_EDITOR } from 'graphql/query/detailQuery';
import { useWorldMarker } from 'hooks/marker/useWorldMarker';
import { useWorldMapLocalStorage } from 'libs/hooks/ownership/useWorldMapLocalStorage';
import { EFonts } from 'libs/styles/styling';
import { useContext, useEffect } from 'react';
import { CtxEdit } from 'session/ctxEdit';
import { CtxModal } from 'session/ctxModal';
import { EWorldColor } from 'static/config/worldVariables';
import { Localization } from 'static/localization/localization';
import styled from 'styled-components/macro';

export interface BookingFieldValue {
    fieldId: string;
    value?: string | boolean;
}

interface Props {
    className?: string;
}

export const EditBooking = (props: Props): JSX.Element => {
    const ctxModal = useContext(CtxModal);
    const ctxEdit = useContext(CtxEdit);
    const worldMarker = useWorldMarker();
    const selectedMarker = worldMarker.getSingleMarkerIfOne();
    const { getTokenForMapId } = useWorldMapLocalStorage();
    const language = Localization.getLanguage();

    const [deleteMutation] = useMutation<DeleteBooking, DeleteBookingVariables>(
        DELETE_BOOKING_MUTATION
    );

    const query = useQuery<BookingQueryEditor, BookingQueryEditorVariables>(BOOKING_QUERY_EDITOR, {
        variables: {
            worldMapId: ctxEdit.map.worldMapId,
            worldMapMarkerId: selectedMarker.id,
            token: getTokenForMapId(ctxEdit.map?.worldMapId)
        },
        fetchPolicy: 'network-only'
    });

    async function deleteActionConfirm() {
        ctxModal.setModal({
            type: EModal.CONFIRM,
            data: {
                text: Localization.DELETE_BOOKING_CONFIRM,
                onConfirm: () => {
                    go();
                }
            } as IConfirmActionModal
        });

        async function go() {
            const worldMapBookingId = query.data.worldMarkerBookingDetails.worldMapBookingId;
            if (!worldMapBookingId) {
                return;
            }
            deleteMutation({
                variables: {
                    worldMapId: ctxEdit.map.worldMapId,
                    worldMapMarkerId: selectedMarker.id,
                    worldMapBookingId
                },
                onCompleted: () => {
                    query.refetch();
                }
            });
        }
    }

    const bookingConfiguration = query?.data?.worldMarkerBookingConfiguration;
    const booking = query?.data?.worldMarkerBookingDetails;

    useEffect(() => {
        if (!query?.data) {
            return;
        }

        ctxEdit.updateWorldMarker(selectedMarker.id, { isBookingAvailable: !Boolean(booking) });
    }, [query?.data?.worldMarkerBookingDetails]);

    if (!bookingConfiguration) {
        return null;
    }

    const fields = bookingConfiguration.fields;

    return (
        <S_EditBooking className={props.className}>
            <SidebarSectionHeader
                icon={{
                    iconName: 'shapes'
                }}
                title={Localization.BOOKING}
            />
            <S_Mid>
                {booking && (
                    <>
                        {fields.map((field) => {
                            const value = booking.data[field.fieldId];
                            if (field.type === EWorldMapBookingConfigurationFieldType.HEADLINE) {
                                return (
                                    <S_Headline key={field.fieldId}>
                                        {getBookingStringTranslation(field.title, language)}
                                    </S_Headline>
                                );
                            }
                            return (
                                <S_Row key={field.fieldId}>
                                    <S_RowLabel>
                                        {getBookingStringTranslation(field.title, language)}:
                                    </S_RowLabel>
                                    {field.type ===
                                        EWorldMapBookingConfigurationFieldType.BOOLEAN && (
                                        <S_RowValue>
                                            {value === '1' ? Localization.YES : Localization.NO}
                                        </S_RowValue>
                                    )}
                                    {(field.type ===
                                        EWorldMapBookingConfigurationFieldType.STRING ||
                                        field.type ===
                                            EWorldMapBookingConfigurationFieldType.EMAIL) && (
                                        <S_RowValue>{value as string}</S_RowValue>
                                    )}
                                </S_Row>
                            );
                        })}
                        <S_RemoveButtonContainer>
                            <ActionButton
                                icon={{ iconName: 'trash-can' }}
                                text={Localization.DELETE_BOOKING}
                                onClick={() => {
                                    deleteActionConfirm();
                                }}
                            />
                        </S_RemoveButtonContainer>
                    </>
                )}
                {!booking && (
                    <>
                        <Message type="info">{Localization.BOOKING_EMPTY_INFO}</Message>
                        {fields.map((field) => {
                            if (field.type === EWorldMapBookingConfigurationFieldType.HEADLINE) {
                                return (
                                    <S_Headline key={field.fieldId}>
                                        {getBookingStringTranslation(field.title, language)}
                                    </S_Headline>
                                );
                            }
                            return (
                                <S_Row key={field.fieldId}>
                                    <S_RowLabel>
                                        {getBookingStringTranslation(field.title, language)}
                                        {field.validation?.required && (
                                            <S_StarRequired>*</S_StarRequired>
                                        )}
                                    </S_RowLabel>
                                    <S_RowValue>
                                        {field.type ===
                                            EWorldMapBookingConfigurationFieldType.BOOLEAN && (
                                            <>{Localization.BOOKING_FIELD_TYPE_CHECKBOX}</>
                                        )}
                                        {field.type ===
                                            EWorldMapBookingConfigurationFieldType.EMAIL && (
                                            <S_RowValue>
                                                {Localization.BOOKING_FIELD_TYPE_EMAIL}
                                            </S_RowValue>
                                        )}
                                        {field.type ===
                                            EWorldMapBookingConfigurationFieldType.STRING && (
                                            <S_RowValue>
                                                {Localization.BOOKING_FIELD_TYPE_TEXT}
                                            </S_RowValue>
                                        )}
                                    </S_RowValue>
                                </S_Row>
                            );
                        })}
                    </>
                )}
            </S_Mid>
        </S_EditBooking>
    );
};

const S_Mid = styled.div`
    padding: 15px 15px 0 15px;
`;

const S_EditBooking = styled.div`
    display: flex;
    width: 100%;
    flex-direction: column;
`;

const S_Headline = styled.div`
    font-family: ${EFonts.Bold};
    margin-top: 26px;
    margin-bottom: 14px;
    &:first-child {
        margin-top: 0;
    }
`;

const S_Row = styled.div`
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    margin-bottom: 10px;
`;

const S_RowLabel = styled.div`
    font-family: ${EFonts.Medium};
    margin-right: 10px;
    width: 50%;
`;

const S_RowValue = styled.div`
    font-family: ${EFonts.Regular};
    width: 50%;
`;

const S_RemoveButtonContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    margin-top: 20px;
`;

const S_StarRequired = styled.span`
    color: #${EWorldColor.RED};
`;
